import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip
} from '@mui/material';
import { LeavePolicyService } from '../../services/leave-policy.service';
import { InstitutionService } from '../../services/institution.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { LeavePolicy, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { useTranslation } from 'react-i18next';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

const LeavePolicyManagement: React.FC = () => {
    const [policies, setPolicies] = useState<LeavePolicy[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [leaveType, setLeaveType] = useState<string>('');
    const [numberOfLeaves, setNumberOfLeaves] = useState<number>(0);
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState<number>(1);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);

    useEffect(() => {
        fetchPolicies();
        fetchInstitutionDetails();
        fetchLeaveTypes();
    }, []);

    const fetchLeaveTypes = async () => {
        try {
            const data = await LeaveTypeService.getByInstitution(true);
            setLeaveTypes(data);
            if (data.length > 0 && !leaveType) {
                setLeaveType(data[0].name);
            }
        } catch (err) {
            // ignore
        }
    };

    const fetchPolicies = async () => {
        try {
            const data = await LeavePolicyService.getPoliciesByInstitution();
            setPolicies(data);
        } catch (err) {
            toast.showApiError(err, t('leavePolicyManagement.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const fetchInstitutionDetails = async () => {
        try {
            const institutions = await InstitutionService.getLoggedInUserInstitutions();
            const selectedId = AuthService.getSelectedInstitution();
            const institution = institutions.content?.find((i: any) => i.id === selectedId);
            if (institution?.fiscalYearStartMonth) {
                setFiscalYearStartMonth(institution.fiscalYearStartMonth);
            }
        } catch (err) {
            // Ignore - default to January
        }
    };

    const handleSaveFiscalMonth = async () => {
        try {
            const institutionId = AuthService.getSelectedInstitution()!!;
            const current = await InstitutionService.getInstitutionById(institutionId);
            await InstitutionService.updateInstitution(institutionId, { ...current, fiscalYearStartMonth });
            toast.showSuccess(t('leavePolicyManagement.fiscalMonthUpdated'));
        } catch (err) {
            toast.showApiError(err, t('leavePolicyManagement.errors.updateFiscalMonth'));
        }
    };

    const handleCreateOrUpdate = async () => {
        try {
            await LeavePolicyService.createOrUpdate({
                institutionId: AuthService.getSelectedInstitution()!!,
                leaveType,
                numberOfLeaves
            });
            setDialogOpen(false);
            setNumberOfLeaves(0);
            await fetchPolicies();
        } catch (err) {
            toast.showApiError(err, t('leavePolicyManagement.errors.save'));
        }
    };

    const handleToggleActive = async (id: string, currentlyActive: boolean) => {
        const nextActive = !currentlyActive;
        const confirmMessage = nextActive
            ? t('leavePolicyManagement.confirmReactivate')
            : t('leavePolicyManagement.confirmDeactivate');
        if (!window.confirm(confirmMessage)) return;
        try {
            await LeavePolicyService.setActiveStatus(id, nextActive);
            setPolicies(prev => prev.map(p =>
                p.id === id ? { ...p, isActive: nextActive } : p
            ));
            toast.showSuccess(nextActive ? t('leavePolicyManagement.reactivated') : t('leavePolicyManagement.deactivated'));
        } catch (err) {
            toast.showApiError(err, nextActive
                ? t('leavePolicyManagement.errors.reactivate')
                : t('leavePolicyManagement.errors.deactivate'));
        }
    };

    const handleEditClick = (policy: LeavePolicy) => {
        setLeaveType(policy.leaveType);
        setNumberOfLeaves(policy.numberOfLeaves);
        setDialogOpen(true);
    };

    if (loading) return <Typography>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>{t('leavePolicyManagement.title')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 3 }}>
                <TextField
                    select
                    label={t('leavePolicyManagement.fiscalYearStartMonth')}
                    value={fiscalYearStartMonth}
                    onChange={(e) => setFiscalYearStartMonth(Number(e.target.value))}
                    sx={{ minWidth: 200 }}
                    size="small"
                >
                    {MONTHS.map((m) => (
                        <MenuItem key={m} value={m}>{t(`common:months.${m}`)}</MenuItem>
                    ))}
                </TextField>
                <Button variant="outlined" onClick={handleSaveFiscalMonth}>
                    {t('leavePolicyManagement.saveFiscalMonth')}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <Button variant="contained" onClick={() => { setLeaveType(leaveTypes[0]?.name ?? ''); setNumberOfLeaves(0); setDialogOpen(true); }}>
                    {t('leavePolicyManagement.addPolicy')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('leavePolicyManagement.columns.leaveType')}</TableCell>
                            <TableCell>{t('leavePolicyManagement.columns.numberOfLeaves')}</TableCell>
                            <TableCell>{t('leavePolicyManagement.columns.status')}</TableCell>
                            <TableCell>{t('leavePolicyManagement.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {policies.map((policy) => {
                            const active = policy.isActive !== false;
                            return (
                            <TableRow key={policy.id} sx={{ opacity: active ? 1 : 0.55 }}>
                                <TableCell>{policy.leaveType}</TableCell>
                                <TableCell>{policy.numberOfLeaves}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={active ? t('common:status.active') : t('common:status.inactive')}
                                        color={active ? 'success' : 'default'}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button size="small" onClick={() => handleEditClick(policy)}>{t('common:actions.edit')}</Button>
                                    <Button
                                        size="small"
                                        color={active ? 'warning' : 'success'}
                                        onClick={() => handleToggleActive(policy.id, active)}
                                    >
                                        {active ? t('leavePolicyManagement.deactivate') : t('leavePolicyManagement.reactivate')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                        {policies.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">{t('leavePolicyManagement.empty')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('leavePolicyManagement.dialogTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            select
                            label={t('leavePolicyManagement.leaveTypeLabel')}
                            value={leaveType}
                            onChange={(e) => setLeaveType(e.target.value)}
                            fullWidth
                        >
                            {leaveTypes.map((lt) => (
                                <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('leavePolicyManagement.numberOfLeavesLabel')}
                            type="number"
                            value={numberOfLeaves}
                            onChange={(e) => setNumberOfLeaves(Number(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={handleCreateOrUpdate}>{t('common:actions.save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeavePolicyManagement;
